/**
 * Subscription Plans Section JavaScript
 * Handles plan selection and subscription functionality
 */

class SubscriptionPlans {
  constructor() {
    this.currentFrequency = 'annual';
    this.init();
  }

  init() {
    this.loadSellingPlans();
    this.bindEvents();
    this.initializeDefaultSelection();
  }

  async loadSellingPlans() {
    const cards = document.querySelectorAll('.subscription-plan-card');
    
    for (const card of cards) {
      const productHandle = card.dataset.productHandle;
      if (!productHandle) continue;
      
      try {
        const response = await fetch(`/products/${productHandle}.js`);
        const product = await response.json();
        
        // Map variants to their selling plans
        product.variants.forEach(variant => {
          if (variant.selling_plan_allocations && variant.selling_plan_allocations.length > 0) {
            const sellingPlanId = variant.selling_plan_allocations[0].selling_plan_id;
            
            // Update data attributes based on variant option
            const optionValue = (variant.option1 || '').toLowerCase();
            
            if (optionValue === 'mensal' || optionValue === 'monthly') {
              card.dataset.monthlySellingPlan = sellingPlanId;
            } else if (optionValue === 'semestral' || optionValue === 'semester') {
              card.dataset.semestralSellingPlan = sellingPlanId;
            } else if (optionValue === 'anual' || optionValue === 'annual') {
              card.dataset.annualSellingPlan = sellingPlanId;
            }
          }
        });
      } catch (error) {
        console.error('Error loading selling plans for', productHandle, error);
      }
    }
  }

  bindEvents() {
    // Handle frequency button clicks
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('frequency-btn')) {
        this.handleFrequencyChange(e.target);
      }
      
      // Handle subscription button clicks
      if (e.target.classList.contains('subscription-btn')) {
        this.handleSubscription(e.target);
      }
    });

    // Handle keyboard navigation for frequency buttons
    document.addEventListener('keydown', (e) => {
      if (e.target.classList.contains('frequency-btn')) {
        this.handleKeyboardNavigation(e);
      }
    });
  }

  initializeDefaultSelection() {
    // Set annual as default frequency
    this.currentFrequency = 'annual';
    this.updateAllPrices('annual');
  }

  handleFrequencyChange(button) {
    const frequency = button.dataset.frequency;
    if (!frequency) return;

    // Update active state on buttons
    const allButtons = document.querySelectorAll('.frequency-btn');
    allButtons.forEach(btn => btn.classList.remove('active'));
    button.classList.add('active');

    // Update frequency and all prices
    this.currentFrequency = frequency;
    this.updateAllPrices(frequency);
  }

  updateAllPrices(frequency) {
    const planCards = document.querySelectorAll('.subscription-plan-card');
    
    planCards.forEach(card => {
      const priceElement = card.querySelector('[data-price-value]');
      const periodElement = card.querySelector('[data-price-period]');
      
      if (!priceElement || !periodElement) return;

      let price = '';

      switch(frequency) {
        case 'monthly':
          price = card.dataset.monthlyPrice;
          break;
        case 'semestral':
          price = card.dataset.semestralPrice;
          break;
        case 'annual':
          price = card.dataset.annualPrice;
          break;
      }

      // Only update if price exists for this frequency
      if (price && price.trim() !== '') {
        priceElement.textContent = price;
        // Period is always "/ mês" now
        periodElement.textContent = this.getPeriodText('monthly');
        
        // Add animation class
        priceElement.classList.add('price-updating');
        setTimeout(() => {
          priceElement.classList.remove('price-updating');
        }, 300);
      }
    });
  }

  getPeriodText(frequency) {
    // Map frequency to period text - these should match the Liquid translations
    const periodMap = {
      'monthly': '/ mês',
      'semestral': '/ semestre',
      'annual': '/ ano'
    };
    
    return periodMap[frequency] || '';
  }


  handleSubscription(button) {
    const planCard = button.closest('.subscription-plan-card');
    if (!planCard) return;

    const planName = button.dataset.planName;
    const selectedFrequency = this.currentFrequency;
    
    // Get the variant ID and selling plan based on selected frequency
    let variantId = null;
    let sellingPlan = null;
    
    switch(selectedFrequency) {
      case 'monthly':
        variantId = planCard.dataset.monthlyVariantId;
        sellingPlan = planCard.dataset.monthlySellingPlan;
        break;
      case 'semestral':
        variantId = planCard.dataset.semestralVariantId;
        sellingPlan = planCard.dataset.semestralSellingPlan;
        break;
      case 'annual':
        variantId = planCard.dataset.annualVariantId;
        sellingPlan = planCard.dataset.annualSellingPlan;
        break;
    }

    if (!variantId || variantId === '') {
      this.showErrorMessage(new Error('Variante não encontrada para esta frequência'));
      return;
    }

    // Store original button text
    const originalText = button.textContent;

    // Add loading state
    button.classList.add('loading');
    button.disabled = true;
    button.textContent = 'Processando...';

    // Add to cart via Shopify Cart API with selling plan
    this.addToCart(variantId, 1, sellingPlan)
      .then(() => {
        // Track analytics event
        this.trackSubscriptionAttempt(planName, selectedFrequency, variantId);
        
        // Redirect to cart
        window.location.href = '/cart';
      })
      .catch((error) => {
        this.showErrorMessage(error);
        // Remove loading state on error
        button.classList.remove('loading');
        button.disabled = false;
        button.textContent = originalText;
      });
  }

  getCurrentPrice(planCard) {
    const priceElement = planCard.querySelector('[data-price-value]');
    return priceElement ? priceElement.textContent.trim() : null;
  }

  addToCart(variantId, quantity = 1, sellingPlan = null) {
    const body = {
      id: variantId,
      quantity: quantity
    };
    
    // Add selling plan if provided (for subscription products)
    if (sellingPlan && sellingPlan !== '') {
      body.selling_plan = parseInt(sellingPlan);
    }
    
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body)
    })
    .then(response => {
      if (!response.ok) {
        return response.json().then(err => {
          throw new Error(err.description || 'Erro ao adicionar ao carrinho');
        });
      }
      return response.json();
    });
  }

  showErrorMessage(error) {
    const message = error.message || 'Ocorreu um erro ao processar sua assinatura. Tente novamente.';
    this.showNotification(message, 'error');
  }

  showNotification(message, type = 'info') {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `subscription-notification subscription-notification--${type}`;
    notification.innerHTML = `
      <div class="subscription-notification__content">
        <span class="subscription-notification__message">${message}</span>
        <button class="subscription-notification__close" aria-label="Fechar notificação">×</button>
      </div>
    `;

    // Add to page
    document.body.appendChild(notification);

    // Handle close button
    const closeBtn = notification.querySelector('.subscription-notification__close');
    closeBtn.addEventListener('click', () => {
      this.removeNotification(notification);
    });

    // Auto remove after 5 seconds
    setTimeout(() => {
      this.removeNotification(notification);
    }, 5000);

    // Animate in
    setTimeout(() => {
      notification.classList.add('subscription-notification--visible');
    }, 100);
  }

  removeNotification(notification) {
    if (notification && notification.parentNode) {
      notification.classList.remove('subscription-notification--visible');
      setTimeout(() => {
        notification.remove();
      }, 300);
    }
  }

  handleKeyboardNavigation(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (e.target.classList.contains('frequency-btn')) {
        e.target.click();
      }
    }
  }

  trackSubscriptionAttempt(planName, frequency, variantId) {
    // Track with analytics service (Google Analytics, etc.)
    if (typeof gtag !== 'undefined') {
      gtag('event', 'add_to_cart', {
        'plan_name': planName,
        'frequency': frequency,
        'variant_id': variantId,
        'event_category': 'subscription',
        'event_label': `${planName} - ${frequency}`
      });
    }

    // Track with other analytics services as needed
    console.log('Subscription added to cart:', { planName, frequency, variantId });
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new SubscriptionPlans();
});

// Add notification styles dynamically
const notificationStyles = `
  .subscription-notification {
    position: fixed;
    top: 20px;
    right: 20px;
    max-width: 400px;
    background: rgb(var(--color-background));
    border: 1px solid rgba(var(--color-foreground), 0.1);
    border-radius: 8px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    z-index: 9999;
    transform: translateX(100%);
    opacity: 0;
    transition: all 0.3s ease;
  }

  .subscription-notification--visible {
    transform: translateX(0);
    opacity: 1;
  }

  .subscription-notification--success {
    border-left: 4px solid #10b981;
  }

  .subscription-notification--error {
    border-left: 4px solid #ef4444;
  }

  .subscription-notification__content {
    padding: 16px;
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  .subscription-notification__message {
    flex: 1;
    font-size: 14px;
    line-height: 1.4;
    color: rgb(var(--color-foreground));
  }

  .subscription-notification__close {
    background: none;
    border: none;
    font-size: 20px;
    cursor: pointer;
    color: rgba(var(--color-foreground), 0.6);
    padding: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: all 0.2s ease;
  }

  .subscription-notification__close:hover {
    background: rgba(var(--color-foreground), 0.1);
    color: rgb(var(--color-foreground));
  }

  @media (max-width: 480px) {
    .subscription-notification {
      left: 20px;
      right: 20px;
      max-width: none;
    }
  }
`;

// Inject styles
const styleSheet = document.createElement('style');
styleSheet.textContent = notificationStyles;
document.head.appendChild(styleSheet);

